import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { API } from "@/App";
import { Search, X, Plus, TrendingUp, TrendingDown, BarChart3, Loader2 } from "lucide-react";

export const CompareFundsPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedFunds, setSelectedFunds] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fundsData, setFundsData] = useState({});

  // Sample fund data for demo
  const sampleFunds = [
    { schemeCode: "119551", schemeName: "Aditya Birla Sun Life Frontline Equity Fund", category: "Large Cap", returns1Y: 24.5, returns3Y: 18.2, returns5Y: 15.8, expenseRatio: 1.25, minSIP: 500 },
    { schemeCode: "120503", schemeName: "HDFC Mid-Cap Opportunities Fund", category: "Mid Cap", returns1Y: 32.1, returns3Y: 22.5, returns5Y: 18.5, expenseRatio: 1.45, minSIP: 500 },
    { schemeCode: "118989", schemeName: "Axis Bluechip Fund", category: "Large Cap", returns1Y: 20.3, returns3Y: 16.8, returns5Y: 14.2, expenseRatio: 1.15, minSIP: 500 },
    { schemeCode: "125354", schemeName: "Mirae Asset Large Cap Fund", category: "Large Cap", returns1Y: 22.7, returns3Y: 17.9, returns5Y: 16.1, expenseRatio: 1.05, minSIP: 500 },
    { schemeCode: "100356", schemeName: "ICICI Prudential Bluechip Fund", category: "Large Cap", returns1Y: 21.4, returns3Y: 17.2, returns5Y: 15.5, expenseRatio: 1.20, minSIP: 500 },
  ];

  useEffect(() => {
    const fundsParam = searchParams.get('funds');
    if (fundsParam) {
      const fundCodes = fundsParam.split(',');
      fundCodes.forEach(code => {
        const fund = sampleFunds.find(f => f.schemeCode === code);
        if (fund && !selectedFunds.find(f => f.schemeCode === code)) {
          setSelectedFunds(prev => [...prev, fund]);
        }
      });
    }
  }, [searchParams]);

  const searchFunds = async () => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    setLoading(true);
    try {
      // Use sample data for demo
      const filtered = sampleFunds.filter(f => 
        f.schemeName.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered);
    } catch (error) {
      console.error("Error searching funds:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      searchFunds();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const addFund = (fund) => {
    if (selectedFunds.length >= 4) {
      return;
    }
    if (!selectedFunds.find(f => f.schemeCode === fund.schemeCode)) {
      setSelectedFunds([...selectedFunds, fund]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const removeFund = (schemeCode) => {
    setSelectedFunds(selectedFunds.filter(f => f.schemeCode !== schemeCode));
  };

  const comparisonMetrics = [
    { key: "returns1Y", label: "1Y Returns", format: (v) => `${v >= 0 ? '+' : ''}${v}%`, isBetter: (a, b) => a > b },
    { key: "returns3Y", label: "3Y Returns (CAGR)", format: (v) => `${v >= 0 ? '+' : ''}${v}%`, isBetter: (a, b) => a > b },
    { key: "returns5Y", label: "5Y Returns (CAGR)", format: (v) => `${v >= 0 ? '+' : ''}${v}%`, isBetter: (a, b) => a > b },
    { key: "expenseRatio", label: "Expense Ratio", format: (v) => `${v}%`, isBetter: (a, b) => a < b },
    { key: "minSIP", label: "Min SIP", format: (v) => `₹${v}`, isBetter: (a, b) => a < b },
  ];

  const getBestValue = (metric) => {
    if (selectedFunds.length === 0) return null;
    return selectedFunds.reduce((best, fund) => {
      const currentVal = fund[metric.key];
      const bestVal = best[metric.key];
      return metric.isBetter(currentVal, bestVal) ? fund : best;
    }, selectedFunds[0]);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="compare-funds-page">
      <Navbar />
      
      <main className="pt-28 pb-16 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
              Compare <span className="gold-text">Funds</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Compare up to 4 mutual funds side by side to make informed investment decisions
            </p>
          </div>

          {/* Search and Add Funds */}
          <Card className="glass-card mb-8">
            <CardContent className="p-6">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  placeholder="Search funds to compare..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-secondary/50 border-white/10"
                  disabled={selectedFunds.length >= 4}
                  data-testid="compare-search-input"
                />
                {loading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 animate-spin text-primary" />
                )}
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
                  {searchResults.map((fund) => (
                    <div
                      key={fund.schemeCode}
                      className="flex items-center justify-between p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 cursor-pointer transition-colors"
                      onClick={() => addFund(fund)}
                      data-testid={`search-result-${fund.schemeCode}`}
                    >
                      <div>
                        <p className="font-medium text-sm">{fund.schemeName}</p>
                        <p className="text-xs text-muted-foreground">{fund.category}</p>
                      </div>
                      <Button size="sm" variant="ghost">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Funds */}
              {selectedFunds.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {selectedFunds.map((fund) => (
                    <Badge
                      key={fund.schemeCode}
                      variant="secondary"
                      className="px-3 py-2 text-sm"
                      data-testid={`selected-fund-${fund.schemeCode}`}
                    >
                      {fund.schemeName.slice(0, 30)}...
                      <button
                        onClick={() => removeFund(fund.schemeCode)}
                        className="ml-2 hover:text-destructive"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}

              {selectedFunds.length >= 4 && (
                <p className="mt-2 text-sm text-yellow-500">Maximum 4 funds can be compared at once</p>
              )}
            </CardContent>
          </Card>

          {/* Quick Add Sample Funds */}
          {selectedFunds.length < 4 && (
            <div className="mb-8">
              <p className="text-sm text-muted-foreground mb-3">Quick add popular funds:</p>
              <div className="flex flex-wrap gap-2">
                {sampleFunds.filter(f => !selectedFunds.find(s => s.schemeCode === f.schemeCode)).slice(0, 5).map((fund) => (
                  <Button
                    key={fund.schemeCode}
                    variant="outline"
                    size="sm"
                    className="border-white/10"
                    onClick={() => addFund(fund)}
                    data-testid={`quick-add-${fund.schemeCode}`}
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    {fund.schemeName.split(' ').slice(0, 3).join(' ')}...
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* Comparison Table */}
          {selectedFunds.length > 0 ? (
            <Card className="glass-card overflow-hidden">
              <CardHeader>
                <CardTitle className="font-serif text-xl flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-primary" />
                  Comparison
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-secondary/30">
                        <th className="text-left py-4 px-6 font-medium text-muted-foreground">Metrics</th>
                        {selectedFunds.map((fund) => (
                          <th key={fund.schemeCode} className="text-center py-4 px-6 min-w-[200px]">
                            <div className="space-y-2">
                              <Link to={`/research/${fund.schemeCode}`} className="hover:text-primary">
                                <p className="font-medium text-sm">{fund.schemeName.slice(0, 40)}</p>
                              </Link>
                              <Badge variant="secondary" className="text-xs">{fund.category}</Badge>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {comparisonMetrics.map((metric, index) => {
                        const best = getBestValue(metric);
                        return (
                          <tr key={metric.key} className={index % 2 === 0 ? "bg-secondary/10" : ""}>
                            <td className="py-4 px-6 font-medium">{metric.label}</td>
                            {selectedFunds.map((fund) => {
                              const value = fund[metric.key];
                              const isBest = best?.schemeCode === fund.schemeCode;
                              const isPositive = metric.key.includes('returns') && value >= 0;
                              
                              return (
                                <td key={fund.schemeCode} className="text-center py-4 px-6">
                                  <div className={`inline-flex items-center gap-1 font-mono ${
                                    isBest ? 'text-primary font-bold' : ''
                                  } ${metric.key.includes('returns') ? (isPositive ? 'text-green-500' : 'text-red-500') : ''}`}>
                                    {metric.key.includes('returns') && (
                                      isPositive ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />
                                    )}
                                    {metric.format(value)}
                                    {isBest && selectedFunds.length > 1 && (
                                      <Badge className="ml-2 bg-primary/20 text-primary text-xs">Best</Badge>
                                    )}
                                  </div>
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="glass-card">
              <CardContent className="p-12 text-center">
                <BarChart3 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-serif text-xl font-semibold mb-2">No Funds Selected</h3>
                <p className="text-muted-foreground mb-6">
                  Search and add funds above to start comparing
                </p>
                <Button variant="outline" className="border-white/10" asChild>
                  <Link to="/research">Browse Funds</Link>
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Disclaimer */}
          {selectedFunds.length > 0 && (
            <p className="mt-6 text-xs text-muted-foreground text-center">
              * Past performance is not indicative of future returns. Returns shown are point-to-point returns.
            </p>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};
